import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
  TextField,
  MenuItem,
  Select,
  Button,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  getCountryOptions,
  getCurrencyForCountryOption,
  getDodoCurrencyOptions,
  isDodoSupportedCurrency,
  normalizeCountryLabel,
} from "../../utils/countryCurrency";

const COUNTRY_OPTIONS = getCountryOptions(); // [{ value: "AE", label: "United Arab Emirates" }, ...]
// Only currencies the payment provider (Dodo) can charge.
const CURRENCY_OPTIONS = getDodoCurrencyOptions();

// Controlled multi-country pricing editor. Builds the pricing[] array
// ([{ country, currency, price }]) for plan creation — one localized price per
// country, rendered to customers verbatim (no conversion).
const PlanPricingEditor = ({ value = [], onChange = () => {} }) => {
  // Holds the full { value: isoCode, label } option while a country is being
  // picked — the ISO code is what resolves the currency; only the label is
  // ever sent to the backend.
  const [country, setCountry] = useState(null);
  const [currency, setCurrency] = useState("");
  const [price, setPrice] = useState("");
  const [error, setError] = useState("");

  const usedCountries = useMemo(
    () => new Set(value.map((v) => normalizeCountryLabel(v.country))),
    [value]
  );

  const handleCountryChange = (option) => {
    setCountry(option);
    // Auto-fill currency from the country's ISO code, but only if the provider
    // supports it; otherwise leave it blank so the user picks a supported
    // currency manually (e.g. USD).
    const mapped = option ? getCurrencyForCountryOption(option) : "";
    setCurrency(mapped && isDodoSupportedCurrency(mapped) ? mapped : "");
  };

  const handleAdd = () => {
    if (!country || String(price).trim() === "") {
      setError("Country, currency and price are all required.");
      return;
    }
    if (!currency) {
      setError("Couldn't determine a currency for this country — please select one.");
      return;
    }
    if (!isDodoSupportedCurrency(currency)) {
      setError(`${currency} isn't supported by the payment provider, so ${country.label} can't be charged.`);
      return;
    }
    if (usedCountries.has(normalizeCountryLabel(country.label))) {
      setError("This country already has a price. Remove it first to change it.");
      return;
    }
    setError("");
    onChange([...value, { country: country.label, currency: String(currency).toUpperCase(), price: Number(price) }]);
    setCountry(null);
    setCurrency("");
    setPrice("");
  };

  const handleRemove = (entryCountry) => {
    onChange(value.filter((v) => normalizeCountryLabel(v.country) !== normalizeCountryLabel(entryCountry)));
  };

  return (
    <Box>
      <Typography sx={{ fontSize: "14px", fontWeight: 600, mb: 0.5 }}>
        Country Pricing
      </Typography>
      <Typography sx={{ fontSize: "12px", color: "#878787", mb: 1.5 }}>
        Add one localized price per country. Prices show to customers exactly as
        entered — no conversion.
      </Typography>

      {value.length > 0 && (
        <Table size="small" sx={{ mb: 1.5 }}>
          <TableHead>
            <TableRow>
              <TableCell>Country</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell align="right">Price</TableCell>
              <TableCell align="right">Remove</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {value.map((entry) => (
              <TableRow key={entry.country}>
                <TableCell>{entry.country}</TableCell>
                <TableCell>{String(entry.currency || "").toUpperCase()}</TableCell>
                <TableCell align="right">{entry.price}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleRemove(entry.country)}>
                    <DeleteOutlineIcon fontSize="small" color="error" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-start" }}>
        <Autocomplete
          size="small"
          options={COUNTRY_OPTIONS.filter((c) => !usedCountries.has(normalizeCountryLabel(c.label)))}
          getOptionLabel={(option) => option?.label || ""}
          isOptionEqualToValue={(option, val) => option?.value === val?.value}
          value={country}
          onChange={(e, v) => handleCountryChange(v)}
          sx={{ width: 220 }}
          renderInput={(params) => <TextField {...params} label="Country" />}
        />
        <Select
          size="small"
          displayEmpty
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
          sx={{ width: 130 }}
          data-testid="pricing-currency-select"
        >
          <MenuItem value="" disabled>Currency</MenuItem>
          {CURRENCY_OPTIONS.map((c) => (
            <MenuItem key={c} value={c}>{c}</MenuItem>
          ))}
        </Select>
        <TextField
          size="small"
          type="number"
          label="Price"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          sx={{ width: 120 }}
          inputProps={{ min: 0 }}
        />
        <Button
          variant="outlined"
          onClick={handleAdd}
          sx={{ textTransform: "none", height: 40 }}
        >
          Add country
        </Button>
      </Box>
      {error && (
        <Typography sx={{ fontSize: "12px", color: "#d32f2f", mt: 0.75 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default PlanPricingEditor;
